import { get, post } from "../../lib/api.ts";
import { uploadRevision, type UploadProgress } from "../../lib/upload.ts";

/** Where a version goes: a project that exists, or one to make first. */
export type UploadTarget =
  { deliverableId: string } | { create: { title: string; tagId?: string | null } };

/** The board takes video and images only; the picker and a drop are held to the same rule. */
export function isUploadable(file: File): boolean {
  return /^(video|image)\//.test(file.type);
}

/**
 * Puts a file into a project as its next version, making the project first when asked to.
 *
 * The one create-then-upload path: the detail pane's form and the board's drop targets both go
 * through it. `onCreated` fires the moment the project exists, before any byte moves, so a caller
 * whose upload then fails still knows there is a project to reuse and a board to refresh.
 */
export async function uploadVersion(args: {
  tripId: string;
  target: UploadTarget;
  file: File;
  notes?: string;
  resumeDraftId?: string;
  onProgress?: (p: UploadProgress) => void;
  onCreated?: (id: string) => void;
}): Promise<string> {
  const base = `/v1/trips/${args.tripId}`;
  let deliverableId: string;
  if ("deliverableId" in args.target) {
    deliverableId = args.target.deliverableId;
  } else {
    const { title, tagId } = args.target.create;
    const made = await post<{ id: string }>(`${base}/deliverables`, {
      title,
      ...(tagId ? { tagId } : {}),
    });
    deliverableId = made.id;
    args.onCreated?.(made.id);
  }
  const nodes = await get<{ items: { id: string }[] }>(`${base}/storage-nodes`);
  const node = nodes.items[0];
  if (!node)
    throw new Error(
      "This trip has no storage destination. New trips get one automatically; ask the platform owner to repair this one.",
    );
  await uploadRevision({
    tripId: args.tripId,
    deliverableId,
    nodeId: node.id,
    file: args.file,
    notes: args.notes ?? "",
    onProgress: args.onProgress ?? (() => {}),
    ...(args.resumeDraftId ? { resumeDraftId: args.resumeDraftId } : {}),
  });
  return deliverableId;
}
